import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import MuiTooltip from '@mui/material/Tooltip';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import { alpha } from '@mui/material/styles';
import {
  FilterList,
  TrendingDown,
  Whatshot,
  AcUnit,
  WbSunny,
  Timeline,
  ThumbDownAlt,
  Restore,
  Phone,
  PhoneInTalk,
  AssignmentInd,
  FilterAlt,
  Clear as ClearIcon,
  Check as CheckIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  TabPanel,
  StyledTabs,
  KPICard,
  SectionHeader,
  ChartCard,
  DataTable,
  StatusChip,
  COLORS,
  CHART_COLORS,
  PageContainer,
} from './components/ReportShared';
import DateRangeFilter, { getDefaultDateRange, getTodayDateRange } from './components/DateRangeFilter';
import useReportData from './hooks/useReportData';
import { fetchLeadFunnel, fetchLeadTemperature, fetchLeadAging, fetchNotInterestedRecovery } from '../../services/reportV2Service';

const TEMP_COLORS = { Hot: COLORS.red, Warm: COLORS.orange, Cold: COLORS.blue };

const NI_REASON_COLOR_MAP = {
  'Budget/Price Concerns': COLORS.red,
  'Location Not Suitable': COLORS.orange,
  'Already Purchased/Decided': COLORS.purple,
  'Not Looking Right Now': COLORS.amber,
  'Timeline/Timing Issues': COLORS.blue,
  'Property Type/Size Mismatch': COLORS.indigo,
  'Family/Personal Reasons': COLORS.pink,
  'Investment Concerns': COLORS.slate[600],
  'Other': COLORS.slate[400],
};

const toSafeKey = (cat) => cat.replace(/\//g, '_').replace(/ /g, '_').replace(/\./g, '');

/** Client-side page size for Not Interested Leads table (same pattern as Conversation Intelligence → Recent AI-Analyzed Calls). */
const NI_LEADS_PAGE_SIZE = 100;

const EMPTY_NI_LEAD_FILTERS = {
  callsMin: '',
  callsMax: '',
  talkTimeMin: '',
  talkTimeMax: '',
  sources: [],
  projects: [],
  niReasons: [],
  agents: [],
};

function parseOptionalNumber(value) {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function LeadPipelineIntelligence() {
  const [tabValue, setTabValue] = useState(0);
  const [funnelDateRange, setFunnelDateRange] = useState(getDefaultDateRange());
  const [tempDateRange, setTempDateRange] = useState(getDefaultDateRange());
  const [agingDateRange, setAgingDateRange] = useState(getDefaultDateRange());
  const [notInterestedDateRange, setNotInterestedDateRange] = useState(getTodayDateRange);

  const { data: funnelData, loading: funnelLoading } = useReportData(
    fetchLeadFunnel, funnelDateRange, {}, tabValue === 0,
  );

  const { data: tempData, loading: tempLoading } = useReportData(
    fetchLeadTemperature, tempDateRange, {}, tabValue === 1,
  );

  const { data: agingData, loading: agingLoading } = useReportData(
    fetchLeadAging, agingDateRange, {}, tabValue === 2,
  );

  const { data: niData, loading: niLoading } = useReportData(
    fetchNotInterestedRecovery, notInterestedDateRange, {}, tabValue === 3,
  );

  const [niLeadsPage, setNiLeadsPage] = useState(1);
  const [draftNiLeadFilters, setDraftNiLeadFilters] = useState(() => ({ ...EMPTY_NI_LEAD_FILTERS }));
  const [appliedNiLeadFilters, setAppliedNiLeadFilters] = useState(() => ({ ...EMPTY_NI_LEAD_FILTERS }));

  const allNILeads = niData?.recentNILeads ?? [];

  const niLeadFilterOptions = useMemo(() => {
    const sources = new Set();
    const projects = new Set();
    const agents = new Set();
    const reasons = new Set();
    const apiCats = niData?.reasonCategories;
    if (Array.isArray(apiCats)) {
      apiCats.forEach((c) => {
        if (c) reasons.add(c);
      });
    }
    for (const r of allNILeads) {
      if (r.source) sources.add(r.source);
      if (r.project) projects.add(r.project);
      if (r.agent) agents.add(r.agent);
      if (r.niReason) reasons.add(r.niReason);
    }
    return {
      sources: Array.from(sources).sort((a, b) => a.localeCompare(b)),
      projects: Array.from(projects).sort((a, b) => a.localeCompare(b)),
      agents: Array.from(agents).sort((a, b) => a.localeCompare(b)),
      reasons: Array.from(reasons).sort((a, b) => a.localeCompare(b)),
    };
  }, [allNILeads, niData?.reasonCategories]);

  const filteredNILeads = useMemo(() => {
    const cmin = parseOptionalNumber(appliedNiLeadFilters.callsMin);
    const cmax = parseOptionalNumber(appliedNiLeadFilters.callsMax);
    const tmin = parseOptionalNumber(appliedNiLeadFilters.talkTimeMin);
    const tmax = parseOptionalNumber(appliedNiLeadFilters.talkTimeMax);
    const src = appliedNiLeadFilters.sources;
    const proj = appliedNiLeadFilters.projects;
    const reasons = appliedNiLeadFilters.niReasons;
    const ag = appliedNiLeadFilters.agents;

    return allNILeads.filter((row) => {
      const calls = Number(row.connectedCalls) || 0;
      if (cmin != null && calls < cmin) return false;
      if (cmax != null && calls > cmax) return false;
      const talk = Number(row.talkTimeMin) || 0;
      if (tmin != null && talk < tmin) return false;
      if (tmax != null && talk > tmax) return false;
      if (src.length && !src.includes(row.source)) return false;
      if (proj.length && !proj.includes(row.project)) return false;
      if (reasons.length && !reasons.includes(row.niReason)) return false;
      if (ag.length && !ag.includes(row.agent)) return false;
      return true;
    });
  }, [allNILeads, appliedNiLeadFilters]);

  const niFilteredTotal = filteredNILeads.length;

  const paginatedNILeads = useMemo(() => {
    const start = (niLeadsPage - 1) * NI_LEADS_PAGE_SIZE;
    return filteredNILeads.slice(start, start + NI_LEADS_PAGE_SIZE);
  }, [filteredNILeads, niLeadsPage]);

  const niFiltersDirty = useMemo(
    () => JSON.stringify(draftNiLeadFilters) !== JSON.stringify(appliedNiLeadFilters),
    [draftNiLeadFilters, appliedNiLeadFilters],
  );

  const applyNiLeadFilters = useCallback((e) => {
    if (e?.preventDefault) e.preventDefault();
    setAppliedNiLeadFilters({ ...draftNiLeadFilters });
    setNiLeadsPage(1);
  }, [draftNiLeadFilters]);

  const clearNiLeadFilters = useCallback(() => {
    setDraftNiLeadFilters({ ...EMPTY_NI_LEAD_FILTERS });
    setAppliedNiLeadFilters({ ...EMPTY_NI_LEAD_FILTERS });
    setNiLeadsPage(1);
  }, []);

  useEffect(() => {
    setNiLeadsPage(1);
    setDraftNiLeadFilters({ ...EMPTY_NI_LEAD_FILTERS });
    setAppliedNiLeadFilters({ ...EMPTY_NI_LEAD_FILTERS });
  }, [notInterestedDateRange.startDate, notInterestedDateRange.endDate]);

  useEffect(() => {
    if (niFilteredTotal === 0) return;
    const maxPage = Math.max(1, Math.ceil(niFilteredTotal / NI_LEADS_PAGE_SIZE));
    if (niLeadsPage > maxPage) {
      setNiLeadsPage(maxPage);
    }
  }, [niFilteredTotal, niLeadsPage]);

  const handleTabChange = (_, v) => setTabValue(v);

  const tabs = [
    'Lead Funnel',
    'Lead Temperature',
    'Lead Aging',
    'Not Interested RCA',
  ];

  return (
    <PageContainer>
      <StyledTabs value={tabValue} onChange={handleTabChange} tabs={tabs} />

      {/* Tab 1: Lead Funnel */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Lead Funnel
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              Total Leads → Assigned → Contacted → Connected → Qualified → Visit → Booking → Not Interested (NI: status or junk stage)
            </Typography>
          </Box>
          <DateRangeFilter value={funnelDateRange} onChange={setFunnelDateRange} />
        </Box>

        {funnelLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: COLORS.teal[600] }} />
          </Box>
        ) : !funnelData ? (
          <Typography sx={{ textAlign: 'center', py: 6, color: COLORS.slate[400] }}>No data available</Typography>
        ) : (() => {
          const kpis = funnelData.kpis || {};
          const funnel = funnelData.funnelData || [];
          const monthlyFunnel = funnelData.monthlyFunnelData || [];
          const bySource = funnelData.bySource || [];
          const byProject = funnelData.byProject || [];
          const byAgent = funnelData.byAgent || [];

          return (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <KPICard title="Total Leads" value={String(kpis.totalLeads || 0)} icon={FilterList} color={COLORS.teal[600]} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <KPICard title="Assigned" value={String(kpis.assigned ?? 0)} subtitle={`${kpis.assignedRate ?? 0}% of leads`} icon={AssignmentInd} color={COLORS.indigo} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <KPICard title="Contacted" value={String(kpis.contacted ?? 0)} subtitle={`${kpis.contactedRate ?? 0}% of leads`} icon={Phone} color={COLORS.teal[500]} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <KPICard title="Connected" value={String(kpis.connected ?? 0)} subtitle={`${kpis.connectedRate ?? 0}% of contacted`} icon={PhoneInTalk} color={COLORS.teal[400]} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <KPICard title="Qualified" value={String(kpis.qualified || 0)} subtitle={`${kpis.qualifiedRate || 0}% of leads`} icon={FilterList} color={COLORS.teal[500]} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <KPICard title="Site Visits" value={String(kpis.siteVisits || 0)} subtitle={`${kpis.visitRate || 0}% qualified→visit`} icon={FilterList} color={COLORS.teal[400]} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <KPICard title="Bookings" value={String(kpis.bookings || 0)} subtitle={`${kpis.bookingRate || 0}% visit→booking`} icon={FilterList} color={COLORS.teal[600]} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <KPICard title="Not Interested" value={String(kpis.notInterested ?? 0)} subtitle={`${kpis.notInterestedRate ?? 0}% of leads`} icon={ThumbDownAlt} color={COLORS.red} />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ChartCard title="Funnel Visualization" subtitle="Decreasing values with stage conversion %" height={440}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={funnel} layout="vertical" margin={{ top: 10, right: 30, left: 108, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <YAxis type="category" dataKey="stage" width={104} tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <Tooltip
                          formatter={(value, name, props) => [
                            props.payload.conversion != null
                              ? `${value} (${props.payload.conversion}% conversion)`
                              : value,
                            name,
                          ]}
                          contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }}
                        />
                        <Bar dataKey="value" fill={COLORS.teal[500]} radius={[0, 4, 4, 0]} name="Leads" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ChartCard title="Monthly Funnel Trend" subtitle="Leads created per month and their funnel progression" height={320}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyFunnel} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <YAxis tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                        <Legend />
                        <Bar dataKey="inquiry" fill={CHART_COLORS[0]} name="Total Leads" />
                        <Bar dataKey="assigned" fill={CHART_COLORS[6]} name="Assigned" />
                        <Bar dataKey="contacted" fill={CHART_COLORS[4]} name="Contacted" />
                        <Bar dataKey="connected" fill={CHART_COLORS[5]} name="Connected" />
                        <Bar dataKey="qualified" fill={CHART_COLORS[1]} name="Qualified" />
                        <Bar dataKey="visit" fill={CHART_COLORS[2]} name="Visit" />
                        <Bar dataKey="booking" fill={CHART_COLORS[3]} name="Booking" />
                        <Bar dataKey="not_interested" fill={CHART_COLORS[7]} name="Not Interested" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ChartCard title="Funnel by Lead Source" subtitle="How each source converts through the pipeline" height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={bySource} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                        <XAxis dataKey="source" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <YAxis tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                        <Legend />
                        <Bar dataKey="total" fill={CHART_COLORS[0]} name="Total" />
                        <Bar dataKey="assigned" fill={CHART_COLORS[6]} name="Assigned" />
                        <Bar dataKey="contacted" fill={CHART_COLORS[4]} name="Contacted" />
                        <Bar dataKey="connected" fill={CHART_COLORS[5]} name="Connected" />
                        <Bar dataKey="qualified" fill={CHART_COLORS[1]} name="Qualified" />
                        <Bar dataKey="visit" fill={CHART_COLORS[2]} name="Visit" />
                        <Bar dataKey="booking" fill={CHART_COLORS[3]} name="Booking" />
                        <Bar dataKey="not_interested" fill={CHART_COLORS[7]} name="Not Interested" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <DataTable
                    columns={[
                      { label: 'Project', field: 'project' },
                      { label: 'Total', field: 'total', align: 'right' },
                      { label: 'Assigned', field: 'assigned', align: 'right' },
                      { label: 'Contacted', field: 'contacted', align: 'right' },
                      { label: 'Connected', field: 'connected', align: 'right' },
                      { label: 'Qualified', field: 'qualified', align: 'right' },
                      { label: 'Visit', field: 'visit', align: 'right' },
                      { label: 'Booking', field: 'booking', align: 'right' },
                      { label: 'Not Int.', field: 'not_interested', align: 'right' },
                      {
                        label: 'Conversion',
                        field: 'total',
                        align: 'right',
                        render: (_, row) => {
                          const rate = row.total ? ((row.booking / row.total) * 100).toFixed(1) : 0;
                          return (
                            <Box component="span" sx={{ bgcolor: rate >= 5 ? 'rgba(34,197,94,0.12)' : rate >= 2 ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)', color: rate >= 5 ? COLORS.green : rate >= 2 ? COLORS.amber : COLORS.red, px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>
                              {rate}%
                            </Box>
                          );
                        },
                      },
                    ]}
                    rows={byProject}
                  />
                </Grid>
              </Grid>

              <DataTable
                columns={[
                  { label: 'Agent', field: 'agent' },
                  { label: 'Total', field: 'total', align: 'right' },
                  {
                    label: 'Assigned',
                    field: 'assigned',
                    align: 'right',
                    render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(99,102,241,0.12)', color: COLORS.indigo, px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                  },
                  {
                    label: 'Contacted',
                    field: 'contacted',
                    align: 'right',
                    render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(20,184,166,0.1)', color: COLORS.teal[600], px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                  },
                  {
                    label: 'Connected',
                    field: 'connected',
                    align: 'right',
                    render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(45,212,191,0.12)', color: COLORS.teal[500], px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                  },
                  {
                    label: 'Qualified',
                    field: 'qualified',
                    align: 'right',
                    render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(20,184,166,0.12)', color: COLORS.teal[600], px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                  },
                  {
                    label: 'Visit',
                    field: 'visit',
                    align: 'right',
                    render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(99,102,241,0.12)', color: COLORS.indigo, px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                  },
                  {
                    label: 'Booking',
                    field: 'booking',
                    align: 'right',
                    render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(34,197,94,0.12)', color: COLORS.green, px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                  },
                  {
                    label: 'Not Int.',
                    field: 'not_interested',
                    align: 'right',
                    render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(239,68,68,0.12)', color: COLORS.red, px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                  },
                  {
                    label: 'Conversion',
                    field: 'conversionRate',
                    align: 'right',
                    render: (v) => (
                      <Box component="span" sx={{ bgcolor: v >= 5 ? 'rgba(34,197,94,0.12)' : v >= 2 ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)', color: v >= 5 ? COLORS.green : v >= 2 ? COLORS.amber : COLORS.red, px: 1, py: 0.5, borderRadius: 1, fontWeight: 700 }}>
                        {v}%
                      </Box>
                    ),
                  },
                ]}
                rows={byAgent}
              />

              <Box sx={{ mt: 3 }}>
                <DataTable
                  columns={[
                    { label: 'Month', field: 'month' },
                    { label: 'Total Leads', field: 'inquiry', align: 'right' },
                    { label: 'Assigned', field: 'assigned', align: 'right' },
                    { label: 'Contacted', field: 'contacted', align: 'right' },
                    { label: 'Connected', field: 'connected', align: 'right' },
                    { label: 'Qualified', field: 'qualified', align: 'right' },
                    { label: 'Visit', field: 'visit', align: 'right' },
                    { label: 'Booking', field: 'booking', align: 'right' },
                    { label: 'Not Int.', field: 'not_interested', align: 'right' },
                  ]}
                  rows={monthlyFunnel}
                />
              </Box>
            </>
          );
        })()}
      </TabPanel>

      {/* Tab 2: Lead Temperature Distribution */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Lead Temperature Distribution
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              Hot / Warm / Cold from lead tags (assigned leads in active stages), same rules as Dashboard → Active Leads by Intent; date range filters by lead creation date
            </Typography>
          </Box>
          <DateRangeFilter value={tempDateRange} onChange={setTempDateRange} />
        </Box>

        {tempLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: COLORS.teal[600] }} />
          </Box>
        ) : !tempData ? (
          <Typography sx={{ textAlign: 'center', py: 6, color: COLORS.slate[400] }}>No data available</Typography>
        ) : (() => {
          const kpis = tempData.kpis || {};
          const pieData = (tempData.temperatureData || []).map((d) => ({ ...d, color: TEMP_COLORS[d.name] || COLORS.slate[400] }));
          const bySource = tempData.bySource || [];
          const byProject = tempData.byProject || [];
          const byAgent = tempData.byAgent || [];

          return (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <KPICard title="Hot" value={String(kpis.hot || 0)} subtitle={`${kpis.hotPct || 0}%`} icon={Whatshot} color={COLORS.red} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <KPICard title="Warm" value={String(kpis.warm || 0)} subtitle={`${kpis.warmPct || 0}%`} icon={WbSunny} color={COLORS.orange} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <KPICard title="Cold" value={String(kpis.cold || 0)} subtitle={`${kpis.coldPct || 0}%`} icon={AcUnit} color={COLORS.blue} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <KPICard title="Total Active" value={String(kpis.totalActive || 0)} icon={FilterList} color={COLORS.teal[600]} />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ChartCard title="Temperature Distribution" subtitle="Proportion of hot, warm, and cold leads" height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                        >
                          {pieData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ChartCard title="Temperature by Source" subtitle="Stacked by lead source" height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={bySource} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                        <XAxis dataKey="source" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <YAxis tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                        <Legend />
                        <Bar dataKey="hot" stackId="a" fill={COLORS.red} name="Hot" />
                        <Bar dataKey="warm" stackId="a" fill={COLORS.orange} name="Warm" />
                        <Bar dataKey="cold" stackId="a" fill={COLORS.blue} name="Cold" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <DataTable
                    columns={[
                      { label: 'Project', field: 'project' },
                      {
                        label: 'Hot',
                        field: 'hot',
                        align: 'right',
                        render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(239,68,68,0.12)', color: COLORS.red, px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                      },
                      {
                        label: 'Warm',
                        field: 'warm',
                        align: 'right',
                        render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(249,115,22,0.12)', color: COLORS.orange, px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                      },
                      {
                        label: 'Cold',
                        field: 'cold',
                        align: 'right',
                        render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(59,130,246,0.12)', color: COLORS.blue, px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                      },
                      {
                        label: 'Total',
                        field: 'project',
                        align: 'right',
                        render: (_, row) => row.hot + row.warm + row.cold,
                      },
                    ]}
                    rows={byProject}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <DataTable
                    columns={[
                      { label: 'Agent', field: 'agent' },
                      {
                        label: 'Hot',
                        field: 'hot',
                        align: 'right',
                        render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(239,68,68,0.12)', color: COLORS.red, px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                      },
                      {
                        label: 'Warm',
                        field: 'warm',
                        align: 'right',
                        render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(249,115,22,0.12)', color: COLORS.orange, px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                      },
                      {
                        label: 'Cold',
                        field: 'cold',
                        align: 'right',
                        render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(59,130,246,0.12)', color: COLORS.blue, px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                      },
                      { label: 'Total', field: 'total', align: 'right' },
                      {
                        label: 'Hot %',
                        field: 'hotPct',
                        align: 'right',
                        render: (v) => (
                          <Box component="span" sx={{ bgcolor: v >= 20 ? 'rgba(34,197,94,0.12)' : v >= 10 ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)', color: v >= 20 ? COLORS.green : v >= 10 ? COLORS.amber : COLORS.red, px: 1, py: 0.5, borderRadius: 1, fontWeight: 700 }}>
                            {v}%
                          </Box>
                        ),
                      },
                    ]}
                    rows={byAgent}
                  />
                </Grid>
              </Grid>
            </>
          );
        })()}
      </TabPanel>

      {/* Tab 3: Lead Aging & Follow-up Delays */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Lead Aging & Follow-up Delays
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              Days since last contact for active leads
            </Typography>
          </Box>
          <DateRangeFilter value={agingDateRange} onChange={setAgingDateRange} />
        </Box>

        {agingLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: COLORS.teal[600] }} />
          </Box>
        ) : !agingData ? (
          <Typography sx={{ textAlign: 'center', py: 6, color: COLORS.slate[400] }}>No data available</Typography>
        ) : (() => {
          const kpis = agingData.kpis || {};
          const AGING_COLORS = [COLORS.green, COLORS.teal[400], COLORS.amber, COLORS.red];
          const agingChart = (agingData.agingData || []).map((d, i) => ({ ...d, color: AGING_COLORS[i] }));
          const byAgent = agingData.byAgent || [];
          const bySource = agingData.bySource || [];
          const dormantTrend = agingData.dormantTrend || [];

          return (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <KPICard title="0-3 days" value={String(kpis.days03 || 0)} icon={Timeline} color={COLORS.green} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <KPICard title="4-7 days" value={String(kpis.days47 || 0)} icon={Timeline} color={COLORS.teal[400]} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <KPICard title="8-14 days" value={String(kpis.days814 || 0)} icon={Timeline} color={COLORS.amber} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <KPICard title="15+ days" value={String(kpis.days15 || 0)} subtitle={`${kpis.dormantPct || 0}% dormant`} icon={Timeline} color={COLORS.red} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <KPICard title="Avg Days" value={`${kpis.avgDaysSinceContact || 0}`} subtitle="since last contact" icon={Timeline} color={COLORS.slate[600]} />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 7 }}>
                  <ChartCard title="Aging Distribution" subtitle="Active leads by days since last contact" height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={agingChart} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                        <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <YAxis tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                        <Bar dataKey="count" name="Leads">
                          {agingChart.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
                {dormantTrend.length > 0 && (
                  <Grid size={{ xs: 12, md: 5 }}>
                    <ChartCard title="Dormant Lead Trend" subtitle="15+ day old leads by month of last contact" height={300}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={dormantTrend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                          <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                          <YAxis tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                          <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                          <Line type="monotone" dataKey="dormant" stroke={COLORS.red} strokeWidth={2} dot={{ r: 4, fill: COLORS.red }} name="Dormant" />
                        </LineChart>
                      </ResponsiveContainer>
                    </ChartCard>
                  </Grid>
                )}
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ChartCard title="Aging by Source" subtitle="Follow-up delays by lead source" height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={bySource} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                        <XAxis dataKey="source" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <YAxis tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                        <Legend />
                        <Bar dataKey="days03" stackId="a" fill={COLORS.green} name="0-3 days" />
                        <Bar dataKey="days47" stackId="a" fill={COLORS.teal[400]} name="4-7 days" />
                        <Bar dataKey="days814" stackId="a" fill={COLORS.amber} name="8-14 days" />
                        <Bar dataKey="days15" stackId="a" fill={COLORS.red} name="15+ days" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
              </Grid>

              <DataTable
                columns={[
                  { label: 'Agent', field: 'agent' },
                  {
                    label: '0-3 days',
                    field: 'days03',
                    align: 'right',
                    render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(34,197,94,0.15)', color: COLORS.green, px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                  },
                  {
                    label: '4-7 days',
                    field: 'days47',
                    align: 'right',
                    render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(45,212,191,0.15)', color: COLORS.teal[600], px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                  },
                  {
                    label: '8-14 days',
                    field: 'days814',
                    align: 'right',
                    render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: COLORS.amber, px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                  },
                  {
                    label: '15+ days',
                    field: 'days15',
                    align: 'right',
                    render: (v) => <Box component="span" sx={{ bgcolor: 'rgba(239,68,68,0.15)', color: COLORS.red, px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}</Box>,
                  },
                  { label: 'Total', field: 'total', align: 'right' },
                ]}
                rows={byAgent}
              />
            </>
          );
        })()}
      </TabPanel>

      {/* Tab 4: Not Interested RCA */}
      <TabPanel value={tabValue} index={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Not Interested — Root Cause Analysis
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              Why leads drop off and where to focus recovery efforts
            </Typography>
          </Box>
          <DateRangeFilter value={notInterestedDateRange} onChange={setNotInterestedDateRange} />
        </Box>

        {niLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: COLORS.teal[600] }} />
          </Box>
        ) : !niData ? (
          <Typography sx={{ textAlign: 'center', py: 6, color: COLORS.slate[400] }}>No data available</Typography>
        ) : (() => {
          const kpis = niData.kpis || {};
          const reasonCats = niData.reasonCategories || [];
          const reasonBreakdown = niData.reasonBreakdown || [];
          const monthlyTrend = niData.monthlyTrend || [];
          const bySource = niData.bySource || [];
          const byProject = niData.byProject || [];
          const byAgent = niData.byAgent || [];

          return (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <KPICard title="Total Not Interested" value={String(kpis.totalNI || 0)} icon={ThumbDownAlt} color={COLORS.red} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <KPICard title="Top Reason" value={kpis.topReason || '-'} subtitle={`${kpis.topReasonPct || 0}% of all`} icon={ThumbDownAlt} color={COLORS.orange} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <KPICard title="Avg Days to NI" value={`${kpis.avgDaysToNI || 0} days`} subtitle="Inquiry → Not Interested" icon={Timeline} color={COLORS.amber} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <KPICard title="Recoverable" value={`${kpis.recoverablePct || 0}%`} subtitle={`${kpis.recoverableCount || 0} of ${kpis.totalNI || 0} leads`} icon={Restore} color={COLORS.green} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <KPICard title="NI Rate" value={`${kpis.niRate || 0}%`} subtitle="of total pipeline" icon={TrendingDown} color={COLORS.slate[600]} />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ChartCard title="Not Interested — Reason Breakdown" subtitle="Top reasons leads are marked not interested" height={340}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={reasonBreakdown} layout="vertical" margin={{ top: 5, right: 30, left: 130, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <YAxis type="category" dataKey="reason" width={120} tick={{ fontSize: 10, fill: COLORS.slate[600] }} />
                        <Tooltip
                          formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, 'Leads']}
                          contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }}
                        />
                        <Bar dataKey="count" fill={COLORS.red} radius={[0, 4, 4, 0]} name="Leads">
                          {reasonBreakdown.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ChartCard title="Monthly Not Interested Trend" subtitle="Reason-wise trend by month" height={340}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyTrend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <YAxis tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                        <Legend />
                        {reasonCats.map((cat, i) => (
                          <Bar key={cat} dataKey={toSafeKey(cat)} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} name={cat} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ChartCard title="Not Interested by Lead Source" subtitle="Which sources produce the most NI leads" height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={bySource} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                        <XAxis dataKey="source" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <YAxis tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                        <Legend />
                        {reasonCats.map((cat, i) => (
                          <Bar key={cat} dataKey={toSafeKey(cat)} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} name={cat} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <DataTable
                    columns={[
                      { label: 'Project', field: 'project' },
                      { label: 'Total NI', field: 'total', align: 'right' },
                      ...reasonCats.slice(0, 3).map((cat) => ({ label: cat.split('/')[0].split(' ')[0], field: toSafeKey(cat), align: 'right' })),
                      {
                        label: 'Top Reason',
                        field: 'topReason',
                        render: (v) => <StatusChip label={v} color={NI_REASON_COLOR_MAP[v] || COLORS.slate[500]} />,
                      },
                    ]}
                    rows={byProject}
                  />
                </Grid>
              </Grid>

              <DataTable
                columns={[
                  { label: 'Agent', field: 'agent' },
                  { label: 'Total NI', field: 'total', align: 'right' },
                  ...reasonCats.slice(0, 4).map((cat, i) => ({
                    label: cat.split('/')[0].split(' ')[0],
                    field: toSafeKey(cat),
                    align: 'right',
                    render: (v) => (
                      <Box component="span" sx={{ bgcolor: `${CHART_COLORS[i % CHART_COLORS.length]}1F`, color: CHART_COLORS[i % CHART_COLORS.length], px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>
                        {v || 0}
                      </Box>
                    ),
                  })),
                  {
                    label: 'Avg Days',
                    field: 'avgDays',
                    align: 'right',
                    render: (v) => <Box component="span" sx={{ bgcolor: v > 10 ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)', color: v > 10 ? COLORS.red : COLORS.green, px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>{v}d</Box>,
                  },
                ]}
                rows={byAgent}
              />

              <Box sx={{ mt: 4 }}>
                <SectionHeader
                  title="Not Interested Leads"
                  subtitle={
                    niFilteredTotal !== allNILeads.length
                      ? `Showing ${niFilteredTotal.toLocaleString()} of ${allNILeads.length.toLocaleString()} leads in range — filters below; click Lead ID or name to open`
                      : niFilteredTotal > NI_LEADS_PAGE_SIZE
                        ? `${niFilteredTotal.toLocaleString()} leads sorted by recoverable % — ${NI_LEADS_PAGE_SIZE} per page; use filters or click Lead ID / name to open`
                        : 'Lead-level detail with budget, requirements, and recovery potential — use filters below; click Lead ID or name to open the lead'
                  }
                />
                <Paper
                  component="form"
                  onSubmit={applyNiLeadFilters}
                  elevation={0}
                  sx={{
                    mb: 2.5,
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: `1px solid ${alpha(COLORS.teal[600], 0.2)}`,
                    boxShadow: '0 8px 32px rgba(15, 118, 110, 0.08)',
                    background: `linear-gradient(180deg, ${alpha(COLORS.teal[50], 0.9)} 0%, #fff 48%)`,
                  }}
                >
                  <Box
                    sx={{
                      height: 4,
                      background: `linear-gradient(90deg, ${COLORS.teal[500]}, ${COLORS.teal[700]}, ${CHART_COLORS[1]})`,
                    }}
                  />
                  <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        gap: 2,
                        mb: 2.5,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          bgcolor: alpha(COLORS.teal[600], 0.12),
                          color: COLORS.teal[700],
                        }}
                      >
                        <FilterAlt sx={{ fontSize: '1.35rem' }} />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.slate[800], fontSize: '1.05rem', mb: 0.25 }}>
                          Refine results
                        </Typography>
                        <Typography variant="body2" sx={{ color: COLORS.slate[500], fontSize: '0.82rem', lineHeight: 1.5 }}>
                          Filter by call count, talk time (minutes), source, project, NI reason, or agent — then click Apply.
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', ml: { xs: 0, sm: 'auto' } }}>
                        {niFiltersDirty && (
                          <Chip
                            label="Unsaved changes"
                            size="small"
                            sx={{
                              bgcolor: alpha(COLORS.amber, 0.12),
                              color: COLORS.amber,
                              fontWeight: 600,
                              fontSize: '0.72rem',
                            }}
                          />
                        )}
                        <Button
                          type="button"
                          variant="outlined"
                          size="medium"
                          startIcon={<ClearIcon sx={{ fontSize: '1.1rem !important' }} />}
                          onClick={clearNiLeadFilters}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: COLORS.slate[300],
                            color: COLORS.slate[700],
                            '&:hover': { borderColor: COLORS.slate[400], bgcolor: COLORS.slate[50] },
                          }}
                        >
                          Clear
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          size="medium"
                          disabled={!niFiltersDirty}
                          startIcon={<CheckIcon sx={{ fontSize: '1.1rem !important' }} />}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 2.5,
                            boxShadow: `0 4px 14px ${alpha(COLORS.teal[600], 0.35)}`,
                            bgcolor: COLORS.teal[600],
                            '&:hover': { bgcolor: COLORS.teal[700] },
                            '&.Mui-disabled': { bgcolor: COLORS.slate[200], color: COLORS.slate[500] },
                          }}
                        >
                          Apply
                        </Button>
                      </Box>
                    </Box>

                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Calls min"
                          type="number"
                          inputProps={{ min: 0 }}
                          value={draftNiLeadFilters.callsMin}
                          onChange={(e) => setDraftNiLeadFilters((p) => ({ ...p, callsMin: e.target.value }))}
                          placeholder="Any"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Phone sx={{ fontSize: '1.1rem', color: COLORS.teal[600], opacity: 0.85 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Calls max"
                          type="number"
                          inputProps={{ min: 0 }}
                          value={draftNiLeadFilters.callsMax}
                          onChange={(e) => setDraftNiLeadFilters((p) => ({ ...p, callsMax: e.target.value }))}
                          placeholder="Any"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Phone sx={{ fontSize: '1.1rem', color: COLORS.teal[600], opacity: 0.85 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Talk time min"
                          type="number"
                          inputProps={{ min: 0, step: 0.1 }}
                          value={draftNiLeadFilters.talkTimeMin}
                          onChange={(e) => setDraftNiLeadFilters((p) => ({ ...p, talkTimeMin: e.target.value }))}
                          placeholder="Any"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Typography variant="caption" sx={{ color: COLORS.slate[400], fontWeight: 600 }}>min</Typography>
                              </InputAdornment>
                            ),
                            startAdornment: (
                              <InputAdornment position="start">
                                <TimerIcon sx={{ fontSize: '1.1rem', color: COLORS.teal[600], opacity: 0.85 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Talk time max"
                          type="number"
                          inputProps={{ min: 0, step: 0.1 }}
                          value={draftNiLeadFilters.talkTimeMax}
                          onChange={(e) => setDraftNiLeadFilters((p) => ({ ...p, talkTimeMax: e.target.value }))}
                          placeholder="Any"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Typography variant="caption" sx={{ color: COLORS.slate[400], fontWeight: 600 }}>min</Typography>
                              </InputAdornment>
                            ),
                            startAdornment: (
                              <InputAdornment position="start">
                                <TimerIcon sx={{ fontSize: '1.1rem', color: COLORS.teal[600], opacity: 0.85 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}>
                          <InputLabel id="ni-filter-source">Source</InputLabel>
                          <Select
                            labelId="ni-filter-source"
                            multiple
                            value={draftNiLeadFilters.sources}
                            onChange={(e) => {
                              const v = e.target.value;
                              setDraftNiLeadFilters((p) => ({
                                ...p,
                                sources: typeof v === 'string' ? v.split(',') : v,
                              }));
                            }}
                            input={<OutlinedInput label="Source" />}
                            renderValue={(sel) => (sel.length === 0 ? 'All sources' : `${sel.length} selected`)}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 320, borderRadius: 2 } } }}
                            disabled={niLeadFilterOptions.sources.length === 0}
                          >
                            {niLeadFilterOptions.sources.map((name) => (
                              <MenuItem key={name} value={name} dense>{name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}>
                          <InputLabel id="ni-filter-project">Project</InputLabel>
                          <Select
                            labelId="ni-filter-project"
                            multiple
                            value={draftNiLeadFilters.projects}
                            onChange={(e) => {
                              const v = e.target.value;
                              setDraftNiLeadFilters((p) => ({
                                ...p,
                                projects: typeof v === 'string' ? v.split(',') : v,
                              }));
                            }}
                            input={<OutlinedInput label="Project" />}
                            renderValue={(sel) => (sel.length === 0 ? 'All projects' : `${sel.length} selected`)}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 320, borderRadius: 2 } } }}
                            disabled={niLeadFilterOptions.projects.length === 0}
                          >
                            {niLeadFilterOptions.projects.map((name) => (
                              <MenuItem key={name} value={name} dense>{name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}>
                          <InputLabel id="ni-filter-ni-reason">NI Reason</InputLabel>
                          <Select
                            labelId="ni-filter-ni-reason"
                            multiple
                            value={draftNiLeadFilters.niReasons}
                            onChange={(e) => {
                              const v = e.target.value;
                              setDraftNiLeadFilters((p) => ({
                                ...p,
                                niReasons: typeof v === 'string' ? v.split(',') : v,
                              }));
                            }}
                            input={<OutlinedInput label="NI Reason" />}
                            renderValue={(sel) => (sel.length === 0 ? 'All reasons' : `${sel.length} selected`)}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 320, borderRadius: 2 } } }}
                            disabled={niLeadFilterOptions.reasons.length === 0}
                          >
                            {niLeadFilterOptions.reasons.map((name) => (
                              <MenuItem key={name} value={name} dense>{name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <FormControl
                          size="small"
                          fullWidth
                          disabled={niLeadFilterOptions.agents.length === 0}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                        >
                          <InputLabel id="ni-filter-agent">Agent</InputLabel>
                          <Select
                            labelId="ni-filter-agent"
                            multiple
                            value={draftNiLeadFilters.agents}
                            onChange={(e) => {
                              const v = e.target.value;
                              setDraftNiLeadFilters((p) => ({
                                ...p,
                                agents: typeof v === 'string' ? v.split(',') : v,
                              }));
                            }}
                            input={<OutlinedInput label="Agent" />}
                            renderValue={(sel) => (sel.length === 0 ? 'All agents' : `${sel.length} selected`)}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 320, borderRadius: 2 } } }}
                          >
                            {niLeadFilterOptions.agents.map((name) => (
                              <MenuItem key={name} value={name} dense>{name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
                <Box
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  }}
                >
                <DataTable
                  maxHeight={560}
                  sx={{ boxShadow: 'none', border: 'none', borderRadius: 0 }}
                  columns={[
                    {
                      label: 'Lead ID',
                      field: 'leadId',
                      render: (v, row) =>
                        row.leadPk != null ? (
                          <Link
                            component={RouterLink}
                            to={`/leads/${row.leadPk}`}
                            underline="hover"
                            sx={{ fontWeight: 600, fontSize: '0.82rem', color: COLORS.teal[700] }}
                          >
                            {v}
                          </Link>
                        ) : (
                          v
                        ),
                    },
                    {
                      label: 'Name',
                      field: 'name',
                      render: (v, row) =>
                        row.leadPk != null ? (
                          <Link
                            component={RouterLink}
                            to={`/leads/${row.leadPk}`}
                            underline="hover"
                            sx={{ fontWeight: 600, fontSize: '0.82rem', color: COLORS.teal[700] }}
                          >
                            {v}
                          </Link>
                        ) : (
                          v
                        ),
                    },
                    { label: 'Project', field: 'project' },
                    { label: 'Source', field: 'source' },
                    { label: 'Agent', field: 'agent' },
                    { label: 'Calls', field: 'connectedCalls', align: 'right' },
                    { label: 'Talktime', field: 'talkTimeMin', align: 'right', render: (v) => `${v} min` },
                    { label: 'Budget', field: 'budgetRange' },
                    { label: 'Requirement', field: 'requirement' },
                    { label: 'Location', field: 'location' },
                    {
                      label: 'NI Reason',
                      field: 'niReason',
                      render: (v) => <StatusChip label={v} color={NI_REASON_COLOR_MAP[v] || COLORS.slate[500]} />,
                    },
                    { label: 'NI Date', field: 'niDate' },
                    {
                      label: 'Days to NI',
                      field: 'daysToNI',
                      align: 'right',
                      render: (v) => (
                        <Box component="span" sx={{ bgcolor: v <= 3 ? 'rgba(239,68,68,0.12)' : v <= 7 ? 'rgba(245,158,11,0.12)' : 'rgba(100,116,139,0.12)', color: v <= 3 ? COLORS.red : v <= 7 ? COLORS.amber : COLORS.slate[600], px: 1, py: 0.5, borderRadius: 1, fontWeight: 600 }}>
                          {v}d
                        </Box>
                      ),
                    },
                    {
                      label: 'Recoverable %',
                      field: 'recoverable',
                      align: 'right',
                      render: (v, row) => {
                        const evidence = row?.recoveryEvidence || [];
                        const strategy = row?.recoveryStrategy || '';
                        const hasDetails = evidence.length > 0 || strategy;
                        const badge = (
                          <Box component="span" sx={{ bgcolor: v >= 60 ? 'rgba(34,197,94,0.12)' : v >= 35 ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)', color: v >= 60 ? COLORS.green : v >= 35 ? COLORS.amber : COLORS.red, px: 1, py: 0.5, borderRadius: 1, fontWeight: 700, cursor: hasDetails ? 'pointer' : 'default' }}>
                            {v}%
                          </Box>
                        );
                        if (!hasDetails) return badge;
                        return (
                          <MuiTooltip
                            arrow
                            placement="left"
                            title={
                              <Box sx={{ maxWidth: 320, p: 0.5 }}>
                                {evidence.length > 0 && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'inherit', display: 'block', mb: 0.5 }}>Evidence</Typography>
                                    {evidence.map((e, i) => (
                                      <Typography key={i} variant="caption" sx={{ display: 'block', color: 'inherit', lineHeight: 1.4 }}>• {e}</Typography>
                                    ))}
                                  </Box>
                                )}
                                {strategy && (
                                  <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'inherit', display: 'block', mb: 0.5 }}>Strategy</Typography>
                                    <Typography variant="caption" sx={{ color: 'inherit', lineHeight: 1.4 }}>{strategy}</Typography>
                                  </Box>
                                )}
                              </Box>
                            }
                          >
                            {badge}
                          </MuiTooltip>
                        );
                      },
                    },
                    {
                      label: 'Recovery strategy',
                      field: 'recoveryStrategy',
                      render: (v) => {
                        const text = v != null && String(v).trim() !== '' ? String(v).trim() : '';
                        if (!text) {
                          return (
                            <Typography variant="body2" sx={{ color: COLORS.slate[400], fontSize: '0.82rem' }}>
                              —
                            </Typography>
                          );
                        }
                        return (
                          <MuiTooltip title={text} arrow placement="left" enterDelay={400}>
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                maxWidth: 300,
                                display: '-webkit-box',
                                WebkitLineClamp: 4,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                whiteSpace: 'normal',
                                color: COLORS.slate[700],
                                fontSize: '0.8rem',
                                lineHeight: 1.45,
                                cursor: 'default',
                              }}
                            >
                              {text}
                            </Typography>
                          </MuiTooltip>
                        );
                      },
                    },
                  ]}
                  rows={paginatedNILeads}
                />
                <TablePagination
                  component="div"
                  count={niFilteredTotal}
                  page={niLeadsPage - 1}
                  onPageChange={(e, newPage) => {
                    e.preventDefault();
                    setNiLeadsPage(newPage + 1);
                  }}
                  rowsPerPage={NI_LEADS_PAGE_SIZE}
                  rowsPerPageOptions={[NI_LEADS_PAGE_SIZE]}
                  onRowsPerPageChange={() => {}}
                  sx={{ borderTop: `1px solid ${COLORS.slate[200]}`, bgcolor: COLORS.slate[50] }}
                />
                </Box>
              </Box>
            </>
          );
        })()}
      </TabPanel>
    </PageContainer>
  );
}
